import { Injectable, inject } from '@angular/core'
import { EntitySignalStore } from '@awork/core/state/signal-store/entitySignalStore'
import { arrayAdd, arrayRemove, arrayUpdate } from '@awork/core/state/signal-store/helpers'
import type { LinkedChat } from '@awork/features/project/models/project.model'
import { Project } from '@awork/features/project/models/project.model'
import { SignalService } from '@awork/_shared/services/signal-service/signal.service'
import type { SyncOptions } from '@awork/core/state/signal-store/types'

@Injectable({ providedIn: 'root' })
export class ProjectStore extends EntitySignalStore<Project> {
  private signalService = inject(SignalService)

  constructor() {
    super({
      name: 'project',
      entityConstructor: project => Project.mapProject(project),
      persistActiveState: false,
      nullableProps: [
        'color',
        'plannedDuration',
        'totalPlannedDurationWithHierarchy',
        'tasksCount',
        'tasksDoneCount',
        'trackedDuration',
        'workflowId',
        'startDate',
        'dueDate',
        'companyId',
        'company',
        'timeBudget',
        'isBillableByDefault',
        'projectTypeId',
        'projectType',
        'isProjectKeyVisible'
      ]
    })

    // In every initialization and reconnection, reset the tasks loaded flag of all projects
    this.resetTasksLoaded()
    this.signalService.connected.subscribe(() => this.resetTasksLoaded())
  }

  preUpdateEntity(storedProject: Readonly<Project>, project: Readonly<Project>): Project {
    const modifiedOn = new Date().getTime()
    const tasksLoaded = project.tasksLoaded === undefined ? storedProject.tasksLoaded : project.tasksLoaded
    const milestones = project.milestones === undefined ? storedProject.milestones : project.milestones

    const imageLastUpdatedOn =
      project.imageLastUpdatedOn === undefined ||
      (project.imageLastUpdatedOn && project.imageLastUpdatedOn < storedProject.imageLastUpdatedOn)
        ? storedProject.imageLastUpdatedOn
        : project.imageLastUpdatedOn

    return {
      ...project,
      modifiedOn,
      tasksLoaded,
      milestones,
      imageLastUpdatedOn
    }
  }

  preAddEntity(newEntity: Readonly<Project>): Project {
    const modifiedOn = new Date().getTime()
    return { ...newEntity, modifiedOn }
  }

  /**
   * Synchronize the store with the data fetched from the API
   * Required only when fixed filters are involved (Ex.: fetching closed projects)
   * Add new projects, update existing project, remove non-existing projects
   * @param {Project[]} projects
   * @param {Project[]} storedProjects
   * @param {SyncOptions<Project>} options
   */
  sync(projects: Project[], storedProjects: Project[], options?: SyncOptions<Project>): void {
    const defaultOptions: SyncOptions<Project> = {
      paging: { page: 1, pageSize: 50 },
      keepProp: 'tasksLoaded'
    }

    super.sync(projects, storedProjects, { ...defaultOptions, ...options })
    this.clearRedactedTrackedDurations(projects)
  }

  /**
   * Synchronize a single project with the data fetched from the API
   * @param {Project} project
   */
  syncEntity(project: Project): void {
    const storedProject = this.entityMap()[project.id]
    let nextProject = storedProject

    if (
      !storedProject ||
      !storedProject.name ||
      project.resourceVersion > (storedProject.resourceVersion || 0) ||
      (project.score && storedProject.score !== project.score) ||
      this.hasRedactedTrackedDuration(storedProject, project)
    ) {
      nextProject =
        project.tasksLoaded === undefined
          ? new Project({ ...project, tasksLoaded: storedProject?.tasksLoaded })
          : project
    }

    super.syncEntity(nextProject)

    if (this.hasRedactedTrackedDuration(storedProject, project)) {
      this.update(project.id, { trackedDuration: undefined })
    }
  }

  /**
   * Clears cached tracked duration when the API sends a redacted project projection.
   * @param {Project[]} projects Projects received from the API.
   */
  private clearRedactedTrackedDurations(projects: Project[]): void {
    projects
      .filter(project => this.hasRedactedTrackedDuration(this.entityMap()[project.id], project))
      .forEach(project => this.update(project.id, { trackedDuration: undefined }))
  }

  /**
   * Returns whether the incoming project redacts a previously cached tracked duration.
   * @param {Readonly<Project> | undefined} storedProject Cached project.
   * @param {Readonly<Project>} incomingProject Project received from the API.
   * @returns {boolean} True when the cached tracked duration must be cleared.
   */
  private hasRedactedTrackedDuration(
    storedProject: Readonly<Project> | undefined,
    incomingProject: Readonly<Project>
  ): boolean {
    const incomingTrackedDuration = incomingProject.trackedDuration as number | null | undefined
    const isTrackedDurationRedacted = incomingTrackedDuration === null || incomingTrackedDuration === undefined

    return !!storedProject && storedProject.trackedDuration != null && isTrackedDurationRedacted
  }

  /**
   * Resets the tasksLoaded flag of all projects.
   */
  resetTasksLoaded(): void {
    this.update(project => project.tasksLoaded, { tasksLoaded: false })
  }

  /**
   * Updates the project's company updateOn to force reload the logo
   * @param {string} companyId
   */
  updateCompanyLogo(companyId: string): void {
    this.update(
      project => project.companyId === companyId,
      project => {
        const company = project.company

        company.updatedOn = new Date()

        return { ...project, company }
      }
    )
  }

  /**
   * Updates the project's updateOn to force reload the logo
   * @param {string} projectId
   */
  updateProjectLogo(projectId: string): void {
    this.update(project => project.id === projectId, {
      imageLastUpdatedOn: new Date()
    })
  }

  /**
   * Adds a linked chat to the store
   * @param {string} projectId
   * @param {LinkedChat} newValue
   */
  addLinkedChat(projectId: string, newValue: LinkedChat): void {
    if (newValue.provider && newValue.channelId) {
      this.update(projectId, ({ linkedChats }) => ({
        linkedChats: arrayAdd(linkedChats, newValue)
      }))
    }
  }

  /**
   * Removes a linked chat from the store based on channelId and provider
   * @param {string} projectId
   * @param {string} channelId
   * @param {string} provider
   */
  removeLinkedChat(projectId: string, channelId: string, provider: string): void {
    const predicate = (linkedChat: LinkedChat) => linkedChat.channelId === channelId && linkedChat.provider === provider

    this.update(projectId, ({ linkedChats }) => ({
      linkedChats: arrayRemove(linkedChats, predicate)
    }))
  }

  /**
   * Updates a linked chat to the store based on channelId and provider
   * @param {string} projectId
   * @param {string} channelId
   * @param {string} provider
   * @param {LinkedChat} updatedValue
   */
  updateLinkedChat(projectId: string, channelId: string, provider: string, updatedValue: LinkedChat): void {
    const predicate = (linkedChat: LinkedChat) => linkedChat.channelId === channelId && linkedChat.provider === provider
    this.update(projectId, ({ linkedChats }) => ({
      linkedChats: arrayUpdate(linkedChats, predicate, updatedValue)
    }))
  }
}
